import { Component, h, Host, Event, EventEmitter, Element, Prop, Watch } from '@stencil/core';
import { addClass } from '../../utils/helpers/helpers';

@Component({
  tag: 'lgr-tab-item',
  styleUrl: 'tabItem.scss',
  shadow: true,
})
export class TabItem {
  @Element() el!: HTMLLgrTabItemElement;
  /**
   * Inverse or not.
   */
  @Prop({ reflect: true }) inverse: boolean = false;
  /**
   * Indicates whether the tab is the currently selected or not.
   */
  @Prop({ reflect: true, mutable: true }) selected: boolean = false;
  /**
   * Disabled or not.
   */
  @Prop({ reflect: true }) disabled: boolean = false;
  /**
   * The id of the content associated with the tab item.
   */
  @Prop({ reflect: true }) target?: string;

  private anchorElement!: HTMLAnchorElement;

  /**
   * Event emitted when the tab item is seleced.
   */
  @Event() lgrTabItemSelected!: EventEmitter<boolean>;

  @Watch('selected')
  handleSelectedStateChange() {
    const content = document.querySelector(`#${this.target}`);
    if (this.selected) {
      content?.removeAttribute('hidden');
      this.anchorElement;
    } else {
      content?.setAttribute('hidden', '');
    }
  }

  componentDidLoad() {
    this.handleSelectedStateChange();
  }

  render() {
    return (
      <Host
        onClick={this.handleClick}
        onKeyDown={this.handleKeydown}
        slot="items"
        class={addClass([this.inverse ? 'inverse' : null])}
        style={{ pointerEvents: this.disabled ? 'none' : undefined }}
      >
        <li role="presentation" aria-role="presentation">
          <a
            ref={el => (this.anchorElement = el as HTMLAnchorElement)}
            tabIndex={this.selected ? 0 : -1}
            role="tab"
            aria-role="tab"
            aria-selected={this.selected.toString()}
            href={this.target}
            class={addClass([this.selected ? 'highlighted' : null, this.inverse ? 'inverse' : null, this.disabled ? 'disabled' : null])}
          >
            <span class="content">
              <slot></slot>
            </span>
          </a>
        </li>
      </Host>
    );
  }

  private handleClick = (event: any) => {
    event.preventDefault();
    this.selected = true;
    this.lgrTabItemSelected.emit();
  };

  private handleKeydown = (event: KeyboardEvent) => {
    if (event.target !== this.el) return;

    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.selectPreviousSibling();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.selectNextSibling();
    }
  };

  private selectPreviousSibling = () => {
    let previousSibling = this.el.previousElementSibling as HTMLLgrTabItemElement;

    while (previousSibling && (previousSibling.disabled || !(previousSibling.tagName.toLowerCase() === 'lgr-tab-item'))) {
      previousSibling = previousSibling.previousElementSibling as HTMLLgrTabItemElement;
    }
    if (previousSibling !== null && previousSibling.tagName.toLowerCase() === 'lgr-tab-item') {
      previousSibling.click();
    }
  };

  private selectNextSibling = () => {
    let nextSibling = this.el.nextElementSibling as HTMLLgrTabItemElement;

    while (nextSibling && (nextSibling.disabled || !(nextSibling.tagName.toLowerCase() === 'lgr-tab-item'))) {
      nextSibling = nextSibling.nextElementSibling as HTMLLgrTabItemElement;
    }
    if (nextSibling !== null && nextSibling.tagName.toLowerCase() === 'lgr-tab-item') {
      nextSibling.click();
    }
  };
}
